import React, { useEffect } from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import {
    useOutletContext,
    Navigate,
    Link,
    useLocation,
    useLoaderData,
} from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { parseBoolean } from "./lib/parseBoolean";
import BackButton from "./BackButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import TutorialLink from "./TutorialLink";
import { findTutorials } from "./lib/findTutorials";

const imageStyle = {
    width: "100%",
    height: "auto",
    maxWidth: "fit-content",
};

function TVMatch() {
    const { matchedTv } = useOutletContext();
    const { state } = useLocation();
    const theme = useTheme();
    const tutorialData = useLoaderData();

    // Check the screen capabilities to swap out the images
    const retinaMatched = useMediaQuery(
        "(-webkit-min-device-pixel-ratio: 2), (min-device-pixel-ratio: 2)"
    );

    // Does the TV have IP or Sat integrated?
    const tvHasSat = parseBoolean(matchedTv.feature_sat);
    const tvHasIP = parseBoolean(matchedTv.feature_ip);

    // Do we have links for IP or Sat tutorials
    const matchedTutorials = findTutorials(
        tutorialData,
        matchedTv.manufacturer
    );

    useEffect(() => {
        document.title = "HD+ Checker | Ja";
        // Google analytics event. Global `gtag` function should be defined.
        if (window.gtag) {
            let type = "scan";
            switch (state.previousPath) {
                case "/input":
                    type = "manual";
                    break;
                case "/scan":
                default:
                    type = "scan";
                    break;
            }

            window.gtag("event", "checker_result", {
                manufacturer: matchedTv.manufacturer,
                model: matchedTv.tvModel,
                state: "success", // 'state': 'fail'
                type,
            });
        }
    }, [state.previousPath, matchedTv.manufacturer, matchedTv.tvModel]);

    if (!matchedTv) {
        return <Navigate to="/input" />;
    }

    return (
        <div className="main-container">
            <Grid2 container>
                <Grid2 xs={1} xsOffset={1} style={{ marginTop: "1rem" }}>
                    <BackButton />
                </Grid2>

                <Grid2 xs={10} xsOffset={1} style={{ marginTop: 0 }}>
                    <Stack spacing={2}>
                        <Grid2
                            xs={12}
                            sx={{
                                justifyContent: "center",
                                alignItems: "center",
                                display: "flex",
                            }}
                        >
                            <Box
                                component="img"
                                alt="Green tick"
                                src="green-tick.svg"
                            />
                        </Grid2>
                        <Grid2
                            xs={12}
                            style={{ textAlign: "center", marginTop: "1.5rem" }}
                        >
                            {matchedTv.manufacturer} {matchedTv.tvModel}
                        </Grid2>

                        {(tvHasSat || tvHasIP) && (
                            <Grid2
                                xs={12}
                                style={{
                                    ...theme.classNames.resultMainText,
                                    color: theme.palette.success.main,
                                    marginTop: "1.5rem",
                                }}
                            >
                                {
                                    "Die HD+ TV-App ist in diesem Gerät integriert."
                                }
                            </Grid2>
                        )}

                        <Grid2
                            xs={12}
                            style={{ marginTop: "1.5rem" }}
                            display="flex"
                            justifyContent="center"
                        >
                            {tvHasSat && (
                                <Grid2 xs={2}>
                                    <Box
                                        component="img"
                                        alt="Sat logo"
                                        src={
                                            retinaMatched
                                                ? "sender-paket-logo-2x@2x.png"
                                                : "sender-paket-logo-2x.png"
                                        }
                                        style={imageStyle}
                                    />
                                </Grid2>
                            )}
                            {tvHasIP && (
                                <Grid2 xs={2} xsOffset={1}>
                                    <Box
                                        component="img"
                                        alt="IP Logo"
                                        src={
                                            retinaMatched
                                                ? "ip-logo-2x@2x.png"
                                                : "ip-logo-2x.png"
                                        }
                                        style={imageStyle}
                                    />
                                </Grid2>
                            )}
                        </Grid2>
                        {state.previousPath === "/input" && (
                            <Grid2
                                xs={12}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                style={{ marginTop: "1rem" }}
                            >
                                <Link to={"/input"} style={{ width: "100%" }}>
                                    <Button
                                        style={{
                                            backgroundColor: "#202020",
                                            width: "100%",
                                        }}
                                        variant="contained"
                                    >
                                        Erneut Eingeben
                                    </Button>
                                </Link>
                            </Grid2>
                        )}
                        {(state.previousPath === "/scan" ||
                            !state.previousPath) && (
                            <Grid2
                                xs={12}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                style={{ marginTop: "1rem" }}
                            >
                                <Link to={"/scan"} style={{ width: "100%" }}>
                                    <Button
                                        style={{
                                            backgroundColor: "#202020",
                                            width: "100%",
                                        }}
                                        variant="contained"
                                    >
                                        Erneut Scannen
                                    </Button>
                                </Link>
                            </Grid2>
                        )}
                    </Stack>
                </Grid2>

                {matchedTutorials && (
                    <TutorialLink
                        manufacturer={matchedTv.manufacturer}
                        tvHasSat={tvHasSat}
                        tvHasIP={tvHasIP}
                        matchedTutorials={matchedTutorials}
                    />
                )}
            </Grid2>
        </div>
    );
}

export default TVMatch;
